<script setup lang="ts">
    import { computed, ref, useId, watch } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { parsePostcodeFinderData, POSTCODE_PATTERN, type FinderPractice } from '../../utils/postcodeFinder/data';
    import PostcodeFinderContact from './PostcodeFinderContact.vue';
    import mapPinIcon from '../../assets/icons/map-pin.svg';

    interface Props {
        readonly contactData: string;
        readonly postcodeMapping: string;
        readonly draft?: boolean;
    }
    const props = defineProps<Props>();
    const { t } = useI18n();
    const instanceId = useId();
    const inputId = `${instanceId}-postcode`;
    const feedbackId = `${instanceId}-feedback`;
    const input = ref<HTMLInputElement>();
    const postcode = ref('');
    const invalidInput = ref(false);
    const submitted = ref(false);
    const result = ref<FinderPractice>();
    const sortedContacts = computed(() =>
        [...(result.value?.contacts ?? [])].sort(
            (first, second) => Number(second.isMainContact) - Number(first.isMainContact)
        )
    );
    const dataset = computed(() => parsePostcodeFinderData(props.contactData, props.postcodeMapping));
    const feedback = computed(() => {
        if (invalidInput.value) return t('postcodeFinder.invalid');
        if (!submitted.value) return '';
        return result.value ? t('postcodeFinder.found', { name: result.value.name }) : t('postcodeFinder.noAssignment');
    });

    function clearResult() {
        submitted.value = false;
        result.value = undefined;
    }

    watch(
        () => [props.contactData, props.postcodeMapping],
        () => {
            clearResult();
            invalidInput.value = false;
        },
        { flush: 'sync' }
    );

    function rejectInput() {
        clearResult();
        invalidInput.value = true;
    }

    function acceptsInsertion(element: HTMLInputElement, inserted: string) {
        const candidate =
            element.value.slice(0, element.selectionStart ?? 0) +
            inserted +
            element.value.slice(element.selectionEnd ?? element.value.length);
        return /^[0-9]{0,5}$/.test(candidate);
    }

    function beforeInput(event: InputEvent) {
        if (event.data !== null && !acceptsInsertion(event.target as HTMLInputElement, event.data)) {
            event.preventDefault();
            rejectInput();
        }
    }

    function paste(event: ClipboardEvent) {
        const pasted = event.clipboardData?.getData('text') ?? '';
        if (!acceptsInsertion(event.target as HTMLInputElement, pasted)) {
            event.preventDefault();
            rejectInput();
        }
    }

    function updateInput(event: Event) {
        const element = event.target as HTMLInputElement;
        clearResult();
        if (!/^[0-9]{0,5}$/.test(element.value)) {
            postcode.value = '';
            element.value = '';
            rejectInput();
            return;
        }
        postcode.value = element.value;
        invalidInput.value = false;
        if (POSTCODE_PATTERN.test(postcode.value)) search();
    }

    function search() {
        clearResult();
        if (!dataset.value) return;
        if (!POSTCODE_PATTERN.test(postcode.value)) {
            invalidInput.value = true;
            input.value?.focus();
            return;
        }
        invalidInput.value = false;
        submitted.value = true;
        const practiceId = dataset.value.postcodeToPractice[postcode.value];
        result.value = practiceId ? dataset.value.practicesById.get(practiceId) : undefined;
    }
</script>

<template>
    <section
        class="postcode-finder"
        :aria-label="t('postcodeFinder.title')"
    >
        <template v-if="dataset">
            <div class="postcode-finder__search-panel">
                <div class="postcode-finder__search-heading">
                    <span
                        class="postcode-finder__icon"
                        aria-hidden="true"
                        ><img
                            :src="mapPinIcon"
                            alt=""
                    /></span>
                    <h2>{{ t('postcodeFinder.title') }}</h2>
                </div>
                <form
                    novalidate
                    class="postcode-finder__form"
                    @submit.prevent="search"
                >
                    <div class="postcode-finder__field">
                        <label :for="inputId">{{ t('postcodeFinder.label') }}</label>
                        <input
                            :id="inputId"
                            ref="input"
                            :value="postcode"
                            type="text"
                            inputmode="numeric"
                            maxlength="5"
                            pattern="[0-9]{5}"
                            autocomplete="off"
                            :aria-invalid="invalidInput"
                            :aria-describedby="feedbackId"
                            required
                            @beforeinput="beforeInput"
                            @paste="paste"
                            @input="updateInput"
                        />
                    </div>
                    <button type="submit">
                        {{ t('postcodeFinder.search') }}
                        <svg
                            aria-hidden="true"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.75"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path d="M5 12h14m-6-6 6 6-6 6" />
                        </svg>
                    </button>
                </form>
            </div>
            <p
                :id="feedbackId"
                class="postcode-finder__feedback"
                :data-error="invalidInput"
                :data-success="Boolean(result)"
                role="status"
                aria-live="polite"
                aria-atomic="true"
            >
                {{ feedback }}
            </p>
            <article
                v-if="result"
                :aria-label="result.name"
                class="postcode-finder__result"
            >
                <header class="postcode-finder__practice">
                    <span
                        class="postcode-finder__icon"
                        aria-hidden="true"
                        ><img
                            :src="mapPinIcon"
                            alt=""
                    /></span>
                    <div>
                        <h2>{{ result.name }}</h2>
                        <address>
                            {{ result.address.street }}<br />{{ result.address.postcode }} {{ result.address.city }}
                        </address>
                    </div>
                </header>
                <ul class="postcode-finder__contacts">
                    <PostcodeFinderContact
                        v-for="(contact, index) in sortedContacts"
                        :key="index"
                        :contact="contact"
                    />
                </ul>
            </article>
        </template>
        <div
            v-else
            class="postcode-finder__search-panel"
            role="status"
        >
            <p>{{ t('postcodeFinder.unavailable') }}</p>
            <p v-if="draft">{{ t('postcodeFinder.configurationError') }}</p>
        </div>
    </section>
</template>

<style scoped lang="scss">
    .postcode-finder {
        --finder-padding: clamp(1rem, 3vw, 2rem);
        --finder-radius: 1.25rem;
        --finder-tint: color-mix(in srgb, var(--brand-primary) 5%, var(--white));

        max-width: 62rem;
        margin-inline: auto;
        overflow-wrap: anywhere;

        &__search-panel {
            padding: var(--finder-padding);
            border-radius: var(--finder-radius);
            background: var(--finder-tint);
        }
        &__search-heading {
            display: flex;
            align-items: center;
            gap: var(--spacing);
            margin-bottom: var(--spacing--1-5);
        }
        &__icon {
            display: grid;
            place-items: center;
            flex: 0 0 3rem;
            width: 3rem;
            height: 3rem;
            border-radius: var(--border-radius);
            background: var(--white);
            img {
                width: 1.5rem;
                height: 1.5rem;
            }
        }
        h2 {
            margin: 0;
            color: var(--black);
            font-family: var(--font-hero-heading);
            font-size: clamp(1.25rem, 2.5vw, 1.65rem);
            font-weight: 700;
            line-height: 1.3;
            letter-spacing: -0.02em;
        }
        &__form {
            display: flex;
            flex-wrap: wrap;
            align-items: end;
            gap: var(--spacing);
        }
        &__field {
            display: grid;
            flex: 1 1 15rem;
            min-width: 0;
            gap: var(--spacing--05);
            label {
                font-size: 0.95rem;
                font-weight: 700;
            }
        }
        &__feedback {
            min-height: 1.5rem;
            margin: var(--spacing) var(--spacing--025);
            color: var(--brand-primary-dark);
            font-size: 0.95rem;
            line-height: 1.5;
            &[data-success='true'] {
                position: absolute;
                width: 1px;
                height: 1px;
                min-height: 0;
                padding: 0;
                margin: -1px;
                overflow: hidden;
                clip-path: inset(50%);
                white-space: nowrap;
            }
            &[data-error='true'] {
                color: var(--color-discount);
            }
        }
        &__result {
            margin-top: var(--spacing--1-5);
            border-radius: var(--finder-radius);
            background: var(--white);
            box-shadow: 0 8px 32px color-mix(in srgb, var(--brand-primary) 5%, transparent);
        }
        &__practice {
            display: flex;
            align-items: start;
            gap: var(--spacing);
            padding: var(--finder-padding);
            > div {
                min-width: 0;
            }
            .postcode-finder__icon {
                background: var(--finder-tint);
            }
        }
        address {
            margin-top: var(--spacing--05);
            color: var(--gray);
            font-size: 1rem;
            font-style: normal;
            line-height: 1.6;
        }
        &__contacts {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(min(100%, 19rem), 1fr));
            gap: var(--spacing);
            list-style: none;
            margin: 0;
            padding: var(--finder-padding);
        }
        input {
            width: 100%;
            min-width: 0;
            min-height: 3.5rem;
            box-sizing: border-box;
            border: 1px solid var(--form-gray);
            border-radius: var(--border-radius);
            padding: var(--spacing--075) var(--spacing);
            background: var(--white);
            color: var(--black);
            font: inherit;
            font-size: 1.125rem;
            font-variant-numeric: tabular-nums;
            letter-spacing: 0.12em;
            &:hover {
                border-color: var(--brand-primary);
                background: color-mix(in srgb, var(--brand-primary) 2%, var(--white));
            }
            &:active {
                border-color: var(--brand-primary-dark);
                background: var(--finder-tint);
            }
            &[aria-invalid='true'] {
                border-color: var(--color-discount);
                background: color-mix(in srgb, var(--color-discount) 8%, var(--white));
            }
        }
        button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 1 1 12rem;
            gap: var(--spacing--1-5);
            min-height: 3.5rem;
            max-width: 100%;
            padding: var(--spacing--075) var(--spacing--1-5);
            border: 0;
            border-radius: var(--border-radius);
            cursor: pointer;
            background: var(--brand-primary);
            color: var(--white);
            font: inherit;
            font-weight: 700;
            svg {
                width: 1.25rem;
                height: 1.25rem;
                flex-shrink: 0;
            }
            &:hover {
                background: var(--brand-primary-dark);
            }
            &:active {
                background: var(--black);
            }
        }
        :is(input, button):focus-visible {
            outline: 3px solid var(--color-focus);
            outline-offset: 3px;
        }
    }
</style>
